import { useEffect, useState, type RefObject } from 'react';

export interface HighlightBox {
  top: number;
  height: number;
  /** Значение `data-tone` подсвеченного пункта — чтобы подложка под опасным
   *  пунктом красилась иначе. */
  tone: string | undefined;
}

/**
 * Следит за подсвеченным пунктом списка и отдаёт его положение.
 *
 * Radix отмечает подсвеченный пункт атрибутом `data-highlighted` — и при
 * наведении, и при ходьбе стрелками. Вместо того чтобы красить каждый пункт
 * отдельно (подсветка гаснет в одном и зажигается в другом), под списком
 * лежит один элемент, который переезжает к подсвеченному. Хук отвечает на
 * вопрос «куда»: слушает события, после которых Radix успел обновить
 * атрибут, и измеряет пункт относительно контейнера.
 */
export function useMovingHighlight(container: RefObject<HTMLElement | null>): HighlightBox | null {
  const [box, setBox] = useState<HighlightBox | null>(null);

  useEffect(() => {
    const root = container.current;

    if (!root) {
      return;
    }

    let frame = 0;

    const measure = () => {
      cancelAnimationFrame(frame);

      // Атрибут проставляется в обработчике того же события, что нас
      // разбудил, поэтому читать его надо на следующем кадре, а не сразу.
      frame = requestAnimationFrame(() => {
        const item = root.querySelector<HTMLElement>('[data-highlighted]');

        if (!item) {
          setBox(null);

          return;
        }

        // offsetTop, а не getBoundingClientRect: при открытии список ещё
        // анимируется из scale(0.9), и прямоугольник на экране в этот момент
        // уменьшен. Лейаут-координаты трансформа не знают — они верны сразу.
        let top = 0;

        for (
          let node: HTMLElement | null = item;
          node && node !== root;
          node = node.offsetParent as HTMLElement | null
        ) {
          top += node.offsetTop;
        }

        setBox({ top, height: item.offsetHeight, tone: item.dataset['tone'] });
      });
    };

    // focusin — клавиатура и первое открытие (Radix ставит фокус на
    // выбранный пункт), pointerover — мышь. Оба всплывают до контейнера.
    root.addEventListener('focusin', measure);
    root.addEventListener('pointerover', measure);
    measure();

    return () => {
      root.removeEventListener('focusin', measure);
      root.removeEventListener('pointerover', measure);
      cancelAnimationFrame(frame);
    };
  }, [container]);

  return box;
}
